import React, { useState, useEffect } from "react";

const SubscriptionWidget = ({ planName, users, price, perUser, planType, checked, discount, onTeamSizeChange }) => {

  // Extract minimum team size from users string (format: "X-Y" or "X+")
  const extractMinTeamSize = (usersString) => {
    if (!usersString) return 31; // Default fallback
    
    const usersStr = usersString.toString();
    
    // Check for the "X-Y" pattern
    const dashPattern = usersStr.match(/^(\d+)-(\d+)$/);
    if (dashPattern) {
      // Return the right-side number (Y) for "X-Y" format
      return parseInt(dashPattern[2]);
    }
    
    // Check for the "X+" pattern
    const plusPattern = usersStr.match(/^(\d+)\+$/);
    if (plusPattern) {
      // Return the number before the plus sign for "X+" format
      return parseInt(plusPattern[1]);
    }
    
    // If no pattern matches, fallback to default
    return 31;
  };
  
  // Use extracted minimum team size
  const minTeamSize = extractMinTeamSize(users);
  
  // Add state for team size with default value from props
  const [teamSize, setTeamSize] = useState(minTeamSize);
  const [inputValue, setInputValue] = useState(minTeamSize.toString());
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  
  // Calculate dynamic price when team size changes (for Enterprise plan)
  const [calculatedPrice, setCalculatedPrice] = useState(price);

  const [perUserPrice, setPerUserPrice] = useState(perUser);
  
  useEffect(() => {

    let planPrice = 0;
    if (planName.toLowerCase() === "enterprise") {
      const effectiveTeamSize = inputValue === "" ? minTeamSize : Math.max(minTeamSize, parseInt(inputValue));
      planPrice = effectiveTeamSize * perUser;

      // Notify parent component about team size changes
      if (onTeamSizeChange) {
        onTeamSizeChange(effectiveTeamSize);
      }

    } else {
      planPrice = price;
    }

    if (planType === "Yearly") {
      planPrice         = planPrice - discount;
      let perUserPrice  = planPrice / teamSize;

      if (planName.toLowerCase() === "enterprise") {
        const effectiveTeamSize = inputValue === "" ? minTeamSize : Math.max(minTeamSize, parseInt(inputValue));
        perUserPrice = planPrice / effectiveTeamSize;
      }
      
      setPerUserPrice(perUserPrice.toFixed(2));
    }else{
      setPerUserPrice(perUser);
    }
    setCalculatedPrice(Number(planPrice).toFixed(2));


    // if (planName?.toLowerCase() === "enterprise" && perUser) {
    //   // Always calculate with at least the minimum team size
    //   const effectiveTeamSize = teamSize === "" ? minTeamSize : Math.max(minTeamSize, teamSize);
    //   setCalculatedPrice((effectiveTeamSize * perUser).toFixed(2));
      
      
    // } else {
    //   setCalculatedPrice(price);
    // }


  }, [teamSize, perUser, planName, price, minTeamSize, onTeamSizeChange]);

  // Handle team size change
  const handleTeamSizeChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Allow empty input for better UX during typing
    if (value === "") {
      setTeamSize("");
      return;
    }
    
    const parsedValue = parseInt(value);
    setTeamSize(parsedValue);
  };
  
  // Handle blur event to ensure valid value when user finishes typing
  const handleBlur = (e) => {
    const parsedValue = parseInt(e.target.value);
    
    // Show toast only when user has finished typing and value is invalid
    if (isNaN(parsedValue) || parsedValue < minTeamSize) {
      setShowToast(true);
      
      // Auto-hide toast after 3 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
      
      // Reset to minimum value
      setTeamSize(minTeamSize);
      setInputValue(minTeamSize.toString());
    }
  };

  // Function to determine styles based on plan name and checked state
  const getPlanStyles = (name, isChecked) => {
    switch (name?.toLowerCase()) {
      case "basic":
        return {
          backgroundColor: isChecked
            ? "border-[#F5B866] border shadow-basic-subscription bg-[#FEF9F2]"
            : "bg-[#FEF9F2]",
          textColor: isChecked ? "text-[#F5B866]" : "text-primary",
        };
      case "premium":
        return {
          backgroundColor: isChecked
            ? "border-[#53E89E] border shadow-premium-subscription bg-[#EAFCF3]"
            : "bg-[#EAFCF3]",
          textColor: isChecked ? "text-[#53E89E]" : "text-primary",
        };
      case "enterprise":
        return {
          backgroundColor: isChecked
            ? "border-[#F38E74] border shadow-enterprise-subscription bg-[#FFEFEB]"
            : "bg-[#FFEFEB]",
          textColor: isChecked ? "text-[#F38E74]" : "text-primary",
        };
      default:
        return {
          backgroundColor: isChecked
            ? "border-[#264254] border shadow-ultra-subscription bg-[#EAEDEE]"
            : "bg-[#EAEDEE]",
          textColor: isChecked ? "text-[#264254]" : "text-primary",
        };
    }
  };

  // Get styles based on plan name and checked state
  const { backgroundColor: bg, textColor: textCol } = getPlanStyles(planName, checked);

  return (
    <div
      className={`group cursor-pointer space-y-6 p-8 flex-col rounded-xl text-center transition-all duration-300 h-76 3xl:h-76 ${bg}`}
    >
      <div className="flex flex-col items-center flex-grow space-y-3 h-2/3">
        <h2 className={`text-lg font-bold ${textCol} xl:text-xl`}>
          {planName}
        </h2>
        <div>
          <h3 className="text-base font-medium xl:text-lg text-secondary-color">
            {users} users
          </h3>
          <h4 className="text-xs text-primary-color-100 2xl:text-sm">
            From £{perUserPrice || "0"}
            {planName !== "Free Trial" && planName !== "Basic" && "*"} per user{" "}
            <br /> per setting
          </h4>
          
          {/* Team size counter for Enterprise plan */}
          {planName?.toLowerCase() === "enterprise" && (
            <div className="mt-3 flex items-center justify-center relative">
              <label className="mr-2 text-sm font-bold text-secondary-color whitespace-nowrap">Team Size:</label>
              <input
                type="number"
                className="w-20 text-center py-1 px-2 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                value={inputValue}
                onChange={handleTeamSizeChange}
                onBlur={handleBlur}
                min={minTeamSize}
              />
              
              {/* Toast notification */}
              {showToast && (
                <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-red-500 text-white px-3 py-1 rounded-md text-xs shadow-md transition-opacity duration-300">
                  Minimum team size must be {minTeamSize} or more
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <h3 className="text-base font-medium text-secondary">{planType}</h3>
        <h2 className="text-2xl font-bold text-secondary lg:text-xl 2xl:text-2xl 3xl:text-3xl">
          <span>
            £{planName?.toLowerCase() === "enterprise" ? calculatedPrice : price}
            {planName !== "Free Trial" && planName !== "Basic" && planName !== "Enterprise" && "*"}
          </span>
        </h2>
        {discount > 0.0 && (
          <h3 className="text-base font-medium text-gray-700">
            You saved <span className="font-semibold text-secondary">£{discount}</span>
          </h3>
        )}
      </div>
    </div>
  );
};

export default SubscriptionWidget;
